import React, { useState, useRef } from 'react';
import {
  Box, Typography, TextField, Grid, Paper, Stack, Button, IconButton,
  AppBar, Toolbar, Alert, Snackbar, CircularProgress, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { supabase } from './supabaseClient';

const EMPTY_FORM = {
  name: '',
  description: '',
  logo_url: '',
  web_url: '',
  phone: '',
  phone_it: '',
  email: ''
};

const Admin = ({ onBack, SimpleGridComponent, initialPartners, extraToolbarActions }) => {
  const [partners, setPartners] = useState(
    [...(initialPartners || [])].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
  );
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLogoFile, setEditLogoFile] = useState(null);
  const [editLogoPreview, setEditLogoPreview] = useState('');
  const [savingEditId, setSavingEditId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const dragId = useRef(null);
  const partnersRef = useRef(partners);
  partnersRef.current = partners;

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Logo upload ───────────────────────────────────────────────────────────────

  const uploadLogo = async (file) => {
    const ext = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const filePath = `logos/${fileName}`;
    const { error } = await supabase.storage.from('cronet-bucket').upload(filePath, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('cronet-bucket').getPublicUrl(filePath);
    return data.publicUrl;
  };

  // ── Add ───────────────────────────────────────────────────────────────────────

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setForm((prev) => ({ ...prev, logo_url: '' }));
  };

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      let finalLogoUrl = form.logo_url.trim() || null;
      if (logoFile) { setUploadingLogo(true); finalLogoUrl = await uploadLogo(logoFile); setUploadingLogo(false); }
        const { data, error } = await supabase.from('partners')
          .insert([{
            name: form.name.trim(),
            description: form.description.trim() || null,
            logo_url: finalLogoUrl,
            web_url: form.web_url.trim() || null,
            phone: form.phone.trim() || null,
            phone_it: form.phone_it.trim() || null,
            email: form.email.trim() || null,
            order_index: partners.length
          }])
          .select().single();
      if (error) throw error;
      setPartners((prev) => [...prev, data]);
      setForm(EMPTY_FORM); setLogoFile(null); setLogoPreview('');
      showSnackbar('Partner byl úspěšně přidán.');
    } catch (error) {
      showSnackbar('Chyba: ' + error.message, 'error');
    } finally { setSaving(false); setUploadingLogo(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      const { error } = await supabase.from('partners').delete().eq('id', id);
      if (error) throw error;
      setPartners((prev) => prev.filter((p) => p.id !== id));
      showSnackbar('Partner byl odstraněn.');
    } catch (error) {
      showSnackbar('Chyba při mazání: ' + error.message, 'error');
    } finally { setDeletingId(null); }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────────

    const startEdit = (partner) => {
      setEditingId(partner.id);
      setEditForm({
        name: partner.name || '',
        description: partner.description || '',
        logo_url: partner.logo_url || '',
        web_url: partner.web_url || '',
        phone: partner.phone || '',
        phone_it: partner.phone_it || '',
        email: partner.email || ''
      });
      setEditLogoFile(null);
      setEditLogoPreview('');
    };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); setEditLogoFile(null); setEditLogoPreview(''); };

  const handleEditLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditLogoFile(file); setEditLogoPreview(URL.createObjectURL(file));
    setEditForm((prev) => ({ ...prev, logo_url: '' }));
  };

  const handleSaveEdit = async (id) => {
    try {
      setSavingEditId(id);
      let finalLogoUrl = editForm.logo_url.trim() || null;
      if (editLogoFile) finalLogoUrl = await uploadLogo(editLogoFile);
        const { data, error } = await supabase.from('partners')
          .update({
            name: editForm.name.trim(),
            description: editForm.description.trim() || null,
            logo_url: finalLogoUrl,
            web_url: editForm.web_url.trim() || null,
            phone: editForm.phone.trim() || null,
            phone_it: editForm.phone_it.trim() || null,
            email: editForm.email.trim() || null
          })
          .eq('id', id).select().single();
      if (error) throw error;
      setPartners((prev) => prev.map((p) => (p.id === id ? data : p)));
      cancelEdit(); showSnackbar('Partner byl aktualizován.');
    } catch (error) {
      showSnackbar('Chyba při ukládání: ' + error.message, 'error');
    } finally { setSavingEditId(null); }
  };

  // ── Drag & drop ───────────────────────────────────────────────────────────────

  const handleDragStart = (e, id) => { dragId.current = id; e.dataTransfer.effectAllowed = 'move'; };

  const handleDragEnter = (e, targetId) => {
    e.preventDefault();
    if (dragId.current === targetId) return;
    setPartners((prev) => {
      const updated = [...prev];
      const from = updated.findIndex((p) => p.id === dragId.current);
      const to = updated.findIndex((p) => p.id === targetId);
      if (from === -1 || to === -1) return prev;
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated;
    });
  };

  const handleDragEnd = async () => {
    const currentOrder = partnersRef.current;
    dragId.current = null;
    const updates = currentOrder.map((p, i) => ({ id: p.id, order_index: i }));
    try {
      setSavingOrder(true);
      for (const { id, order_index } of updates) {
        const { error } = await supabase.from('partners').update({ order_index }).eq('id', id);
        if (error) throw error;
      }
      setPartners(currentOrder.map((p, i) => ({ ...p, order_index: i })));
      showSnackbar('Pořadí bylo uloženo.');
    } catch (error) {
      showSnackbar('Chyba při ukládání pořadí: ' + error.message, 'error');
    } finally { setSavingOrder(false); }
  };

  const addPreviewSrc = logoPreview || form.logo_url;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', display: 'flex', flexDirection: 'column' }}>

      {/* Top Bar */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: 'black', textTransform: 'none' }}>
            Zpět na web
          </Button>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 900, color: 'black' }}>
            Control <span style={{ color: '#0066FF' }}>Panel</span>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {savingOrder && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#0066FF', fontSize: '0.8rem' }}>
                <CircularProgress size={14} /> Ukládám pořadí...
              </Box>
            )}
            {/* Injected actions (e.g. logout button) */}
            {extraToolbarActions}
            <Box sx={{ px: 2.5, py: 0.5, bgcolor: '#0066FF', color: 'white', borderRadius: '20px', fontWeight: 600, fontSize: '0.85rem' }}>
              {partners.length} {partners.length === 1 ? 'partner' : partners.length < 5 ? 'partneři' : 'partnerů'}
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>

        {/* LEFT - Add form */}
        <Box sx={{ width: '380px', flexShrink: 0, bgcolor: 'white', borderRight: '1px solid #e0e0e0', p: 4, overflowY: 'auto' }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>Přidat partnera</Typography>
          <Stack spacing={2.5}>
            <TextField label="Název firmy *" fullWidth size="small" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="Popis" multiline rows={4} fullWidth size="small" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}
              sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              {logoFile ? `✅ ${logoFile.name}` : 'Nahrát logo ze souboru'}
              <input type="file" hidden accept="image/*" onChange={handleLogoSelect} />
            </Button>
            <TextField
              label="Telefon CZ"
              fullWidth
              size="small"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+420 123 456 789"
            />
            <TextField
              label="Telefon IT"
              fullWidth
              size="small"
              value={form.phone_it}
              onChange={(e) => setForm({ ...form, phone_it: e.target.value })}
              placeholder="+39 02 1234 5678"
            />
            <TextField
              label="E-mail"
              fullWidth
              size="small"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="partner@firma.cz"
            />
            <TextField label="…nebo vložit Logo URL" fullWidth size="small" value={form.logo_url}
              onChange={(e) => { setForm({ ...form, logo_url: e.target.value }); if (e.target.value) { setLogoFile(null); setLogoPreview(''); } }}
              placeholder="https://example.com/logo.png" disabled={!!logoFile} />
            {addPreviewSrc && (
              <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: '8px', textAlign: 'center', bgcolor: '#f5f5f5', position: 'relative' }}>
                <img src={addPreviewSrc} alt="Preview" style={{ maxHeight: '60px', maxWidth: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <IconButton size="small" onClick={() => { setLogoFile(null); setLogoPreview(''); setForm({ ...form, logo_url: '' }); }}
                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: '#eee' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <TextField label="Web URL" fullWidth size="small" value={form.web_url}
              onChange={(e) => setForm({ ...form, web_url: e.target.value })} placeholder="https://example.com" />
            <Button variant="contained" fullWidth onClick={handleAdd} sx={{ py: 1.5, fontWeight: 700, mt: 2 }}
              startIcon={(saving || uploadingLogo) ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={!form.name.trim() || saving}>
              {uploadingLogo ? 'Nahrávám logo...' : saving ? 'Ukládám...' : 'Přidat do databáze'}
            </Button>
          </Stack>
        </Box>

        {/* RIGHT - Partners grid */}
        <Box sx={{ flexGrow: 1, p: 4, overflowY: 'auto', bgcolor: '#f8f9fa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Portfolio partnerů</Typography>
            <Typography variant="body2" sx={{ color: '#999', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DragIndicatorIcon fontSize="small" /> Přetažením změníte pořadí
            </Typography>
          </Box>

          {partners.length > 0 ? (
            <Grid container spacing={3}>
              {partners.map((p, index) => (
                <Grid item xs={12} sm={6} lg={4} key={p.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, p.id)}
                  onDragEnter={(e) => handleDragEnter(e, p.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
                >
                  <Box sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    position: 'relative', overflow: 'hidden', transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: '0 8px 30px rgba(0,102,255,0.12)' } }}>

                    {/* Order badge */}
                    <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 10, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DragIndicatorIcon sx={{ color: '#ccc', fontSize: 18 }} />
                      <Box sx={{ px: 1, py: 0.2, bgcolor: '#f0f0f0', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 700, color: '#999' }}>
                        #{index + 1}
                      </Box>
                    </Box>

                    {/* Edit + Delete */}
                    {editingId !== p.id && (
                      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => startEdit(p)}
                          sx={{ bgcolor: '#0066FF', color: 'white', '&:hover': { bgcolor: '#0052CC' }, boxShadow: 2 }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(p.id)} disabled={deletingId === p.id}
                          sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, '&:disabled': { bgcolor: '#ccc' }, boxShadow: 2 }}>
                          {deletingId === p.id ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    )}

                    {/* Edit form */}
                    {editingId === p.id ? (
                      <Box sx={{ p: 3, pt: 5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#0066FF' }}>Upravit partnera</Typography>
                        <Stack spacing={2}>
                          <TextField label="Název *" fullWidth size="small" value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                          <TextField label="Popis" multiline rows={3} fullWidth size="small" value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                          <Button variant="outlined" component="label" size="small"
                            startIcon={<AddPhotoAlternateIcon />} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.8rem' }}>
                            {editLogoFile ? `✅ ${editLogoFile.name}` : 'Změnit logo'}
                            <input type="file" hidden accept="image/*" onChange={handleEditLogoSelect} />
                          </Button>
                            <TextField
                              label="Telefon CZ"
                              fullWidth
                              size="small"
                              value={editForm.phone}
                              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            />
                            <TextField
                              label="Telefon IT"
                              fullWidth
                              size="small"
                              value={editForm.phone_it}
                              onChange={(e) => setEditForm({ ...editForm, phone_it: e.target.value })}
                            />
                            <TextField
                              label="E-mail"
                              fullWidth
                              size="small"
                              value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            />
                          <TextField label="…nebo Logo URL" fullWidth size="small" value={editForm.logo_url}
                            onChange={(e) => { setEditForm({ ...editForm, logo_url: e.target.value }); if (e.target.value) { setEditLogoFile(null); setEditLogoPreview(''); } }}
                            disabled={!!editLogoFile} />
                          {(editLogoPreview || editForm.logo_url) && (
                            <Box sx={{ p: 1.5, border: '1px dashed #ccc', borderRadius: '8px', textAlign: 'center', bgcolor: '#f9f9f9' }}>
                              <img src={editLogoPreview || editForm.logo_url} alt="Preview"
                                style={{ maxHeight: '50px', maxWidth: '100%', objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            </Box>
                          )}
                          <TextField label="Web URL" fullWidth size="small" value={editForm.web_url}
                            onChange={(e) => setEditForm({ ...editForm, web_url: e.target.value })} />
                          <Divider />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="contained" size="small" fullWidth onClick={() => handleSaveEdit(p.id)}
                              disabled={!editForm.name.trim() || savingEditId === p.id}
                              startIcon={savingEditId === p.id ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                              sx={{ fontWeight: 700, textTransform: 'none' }}>
                              {savingEditId === p.id ? 'Ukládám...' : 'Uložit'}
                            </Button>
                            <Button variant="outlined" size="small" onClick={cancelEdit} sx={{ textTransform: 'none', minWidth: 80 }}>
                              Zrušit
                            </Button>
                          </Box>
                        </Stack>
                      </Box>
                    ) : (
                      <Box sx={{ pt: 6, px: 1, pb: 1 }}>
                        <SimpleGridComponent partners={[p]} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 8, textAlign: 'center', bgcolor: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
              <Typography variant="h6" sx={{ mb: 1, opacity: 0.6 }}>Zatím žádní partneři</Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>Přidejte svého prvního partnera pomocí formuláře vlevo</Typography>
            </Paper>
          )}
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Admin;
